import React, { useEffect, useState, useMemo } from "react";
import { Typography } from "@mui/material";
import SelectField from "../../../components/form-fields/select-field/select-field";
import ProjectResources from "./project-resources/project-resources";
import ChangeRateTypeModal from "../components/change-rate-type-modal/change-rate-type-modal";
import {
  BUILDER_ALERTS_MESSAGES,
  DEFAULT_RESOURCE,
} from "../milestone/utils/budget.utils";
import { useRateCard } from "../../../hooks/useRateCards";
import { createRolesMap } from "../../../utils/rateCard";
import { v4 as uuid } from "uuid";
import { useAuth } from "../../../context/AuthContext";
import { isSomething } from "../../../utils/utils";
import TextInputField from "../../../components/form-fields/text-input-field/text-input-field";

const DrawerContent = ({
  isCollapsed,
  project,
  company,
  resources,
  setResources,
  deliverables,
  setDeliverables,
  loggedRolesList,
  rateOption,
  onRateOptionChange,
}) => {
  const { userPermissions } = useAuth();
  const [isDrawerInitialized, setIsDrawerInitialized] = useState(false);
  const [showChangeRateModal, setShowChangeRateModal] = useState("");

  // New state to hold pending selection
  const [pendingRateOption, setPendingRateOption] = useState(null);
  const [pendingRolesMap, setPendingRolesMap] = useState({});

  useEffect(() => {
    if (!isDrawerInitialized && isSomething(project)) {
      const newDeliverables = project.deliverables || [];
      const newResources = project.resources || [];

      if (!resources || resources.length === 0) {
        if (newResources.length === 0) {
          const defaultRes = DEFAULT_RESOURCE(uuid());
          setResources([defaultRes]);
          handleAddResourceInDeliverables({
            id: defaultRes.id,
            hours: 0,
            isEnabled: false,
          });
        } else {
          setResources(newResources);
        }
      }

      if (!deliverables || deliverables.length === 0)
        setDeliverables(newDeliverables);

      if (!rateOption)
        onRateOptionChange(project.rateOption || company?.rateCard || "");

      setIsDrawerInitialized(true);
    }
  }, [isDrawerInitialized, project, rateOption]);

  const handleAddResourceInDeliverables = (val) => {
    const updated = deliverables.map((del) => {
      const updatedTasks = del.tasks.map((task) => ({
        ...task,
        resources: [...task.resources, val],
      }));
      return { ...del, tasks: updatedTasks };
    });
    setDeliverables(updated);
  };

  const handleResourceCleanUpInDeliverables = (resourceId) => {
    const updated = deliverables.map((del) => {
      const updatedTasks = del.tasks.map((task) => ({
        ...task,
        resources: task.resources.filter((x) => x.id !== resourceId),
      }));
      return { ...del, tasks: updatedTasks };
    });
    setDeliverables(updated);
  };

  const { data: allRateCards = [] } = useRateCard();

  const selectedCardId =
    rateOption && rateOption !== "flat" ? rateOption : company?.rateCard;

  const cardObj = useMemo(
    () => allRateCards.find((c) => c.id === selectedCardId) || {},
    [allRateCards, selectedCardId]
  );

  const fetchedRolesList = useMemo(() => createRolesMap(cardObj), [cardObj]);

  const rateOptions = useMemo(() => {
    const options = allRateCards.map((card) => ({
      label: card.name,
      value: card.id,
    }));
    return [...options, { label: "Flat Rate", value: "flat" }];
  }, [allRateCards]);

  const applyRateOption = (value) => {
    if (value === "flat") {
      setPendingRateOption("flat");
      setShowChangeRateModal(
        BUILDER_ALERTS_MESSAGES["change-rate-flat"].description
      );
    } else {
      const selectedCard = allRateCards.find((c) => c.id === value) || {};
      const roleMap = createRolesMap(selectedCard);
      setPendingRateOption(value);
      setPendingRolesMap(roleMap);
      setShowChangeRateModal(
        BUILDER_ALERTS_MESSAGES["change-rate-default"].description
      );
    }
  };

  const handleConfirmRateChange = () => {
    if (pendingRateOption === "flat") {
      setResources((prev) => prev.map((r) => ({ ...r, rate: 0 })));
    } else {
      setResources((prev) =>
        prev.map((r) => ({
          ...r,
          rate: pendingRolesMap?.[r.role]?.rate ?? "",
        }))
      );
    }
    onRateOptionChange(pendingRateOption);
    setPendingRateOption(null);
    setPendingRolesMap({});
    setShowChangeRateModal("");
  };

  const handleCancelRateChange = () => {
    setPendingRateOption(null);
    setPendingRolesMap({});
    setShowChangeRateModal("");
  };

  return (
    <div className="content expanded">
      <div
        className="rate"
        style={{ display: "flex", alignItems: "center", gap: "16px" }}
      >
        <Typography variant="body5">Rate(s):</Typography>
        <SelectField
          boldValue
          placeholder="Select card"
          value={rateOption}
          options={rateOptions}
          noBorder
          onChange={(e) => applyRateOption(e.target.value)}
        />

        {rateOption === "flat" && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <TextInputField
              inputType={"number"}
              value={Number(resources?.[0]?.rate)}
              onChange={(event) => {
                setResources((prev) =>
                  prev.map((resource) => ({
                    ...resource,
                    rate: event.target.value,
                  }))
                );
              }}
            />
          </div>
        )}
      </div>

      <div className="role">
        <ProjectResources
          project={project}
          resources={resources}
          setResources={setResources}
          deliverables={deliverables}
          setDeliverables={setDeliverables}
          rateOption={rateOption}
          fetchedRolesList={fetchedRolesList}
          loggedRolesList={loggedRolesList}
          handleAddResourceInDeliverables={handleAddResourceInDeliverables}
          handleResourceCleanUpInDeliverables={
            handleResourceCleanUpInDeliverables
          }
          isGeneralUser={userPermissions === "General"}
          isCollapsed={isCollapsed}
        />
      </div>

      {showChangeRateModal && (
        <ChangeRateTypeModal
          message={showChangeRateModal}
          onClose={handleCancelRateChange}
          onConfirm={handleConfirmRateChange}
        />
      )}
    </div>
  );
};

export default DrawerContent;
